import styled from '@emotion/styled';

import { css } from '@emotion/react';
import {
    breakpoints,
    colors,
    colorVariables,
    gutterSizes,
    mediaQuery,
} from '@gumtree/ui-library/src/base/theme';
import { Button, Container, Grid, Icon, Logo, PostAdButton } from '@gumtree/ui-library';
import { Wrapper as NavLoginButtonWrapper } from './nav-bar-login-button';
import {
    MhhNavBarButtonOverride,
    MhhNavBarIconOverride,
    NavBarButtonOverride,
} from './common.style';

const unforwarded = {
    mobileCondition: true,
    searchOpen: true,
    isLogInToolTipShown: true,
    mhhStyle: true,
};
function shouldForwardProp(p: string) {
    return !unforwarded[p];
}

const mhhActionItemSize = '32px';
const mhhActionHeight = '42px';

export const NavBarContainer = styled('nav', { shouldForwardProp })<{ mhhStyle?: boolean }>`
    background-color: ${colors.blade};
    padding: ${gutterSizes.base} 0;
    position: relative;
    padding: 0;

    .login-button {
        display: none;

        ${mediaQuery.from(breakpoints.medium)} {
            display: inline-block;
        }
    }

    ${mediaQuery.between(breakpoints.xsmall, breakpoints.small)} {
        .login-button {
            display: none;
        }
    }

    ${({ mhhStyle }) => mhhStyle && css`
        padding: ${gutterSizes.large};
    `}
`;

export const NavBarInnerContainer = styled(Container, { shouldForwardProp })<{ mhhStyle?: boolean }>`
    ${({ mhhStyle }) => mhhStyle && css`
        padding: 0;
    `}
`;

export const NavBarGridContainer = styled(Grid, { shouldForwardProp })<{ mhhStyle?: boolean }>`
    ${({ mhhStyle }) => mhhStyle && css`
        margin: 0;
    `}
`;

export const NavBarGridItem = styled(Grid, { shouldForwardProp })<{ mhhStyle?: boolean }>`
    ${({ mhhStyle }) => mhhStyle && css`
        align-items: center;
        flex-wrap: nowrap;
        padding: 0;
    `}
`;

export const NavLogo = styled(Logo, { shouldForwardProp })<{ mhhStyle?: boolean }>`
    display: flex;
    align-items: center;

    .logo-text {
        display: none;

        ${mediaQuery.from(breakpoints.small)} {
            display: flex;
        }
    }

    ${({ mhhStyle }) => mhhStyle && css`
        .logo-image svg {
            width: 24px;
            height: 30px;
        }
    `}
`;

export const MhhNavIcon = styled(Icon)`
    ${MhhNavBarIconOverride}
`;

export const StyledSearchButton = styled(Button, { shouldForwardProp })<{ mhhStyle?: boolean }>`
    && {
        box-sizing: border-box;
        ${NavBarButtonOverride}

        ${({ mhhStyle }) => mhhStyle && css`
            ${MhhNavBarButtonOverride}
        `}
    }
`;

type NavPostAdButtonStyledProps = {
    mobileCondition: boolean;
    mhhStyle?: boolean;
};

export const NavPostAdButton = styled(PostAdButton, {
    shouldForwardProp,
})<NavPostAdButtonStyledProps>`
    && {
        ${NavBarButtonOverride}
        text-align: center;

        ${({ mhhStyle }) => mhhStyle && css`
            ${MhhNavBarButtonOverride}

            > [aria-hidden="true"] {
                ${MhhNavBarIconOverride}
            }
        `}

        &:hover {
            color: ${colors.bark40};

            .icon--pin {
                transform: rotate(30deg);
                color: ${colors.white};
            }

            .icon--tag-solid {
                &:after {
                    background: ${colors.bark40};
                }
            }
        }

        > .icon--pin {
            width: 100%;

            color: ${colorVariables.mainPrimary};
            transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
    }
`;

export const sellMyCarButtonCss = (theme, mhhStyle = false) => css`
    && {
        ${NavBarButtonOverride}

        ${mhhStyle && css`
            ${MhhNavBarButtonOverride}

            > [aria-hidden="true"] {
                ${MhhNavBarIconOverride}
            }
        `}

        &:hover {
            color: ${colors.white};

            .icon--car {
                transform: rotate(30deg);
                color: ${colors.white};
            }
        }

        > .icon--car {
            width: 100%;
            color: ${theme.palette.iconBtn.ctaIcon};
            transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
    }
`;

export const StyledGrid = styled(Grid)`
    flex-wrap: wrap;

    ${mediaQuery.from(breakpoints.large)} {
        flex-wrap: nowrap;
    }
`;

const WrapperHeight = '60px';
const NavBarButtonsGutter = gutterSizes.large;
export const NavLogoWrapper = styled('div', { shouldForwardProp })<{ mhhStyle?: boolean }>`
    display: flex;
    align-items: center;
    height: ${WrapperHeight};

    ${({ mhhStyle }) => mhhStyle && css`
        flex: 1 0 0;
        min-width: 0;
        gap: ${gutterSizes.medium};
        height: ${mhhActionHeight};
    `}
`;

export const ButtonsWrapper = styled('div', { shouldForwardProp })<{ mhhStyle?: boolean }>`
    display: flex;
    justtify-content: flex-end;
    align-items: center;
    margin-left: -${NavBarButtonsGutter};
    height: ${WrapperHeight};
    gap: ${gutterSizes.large};

    ${mediaQuery.from(breakpoints.large)} {
        order: 2;
    }

    ${NavLoginButtonWrapper} {
        margin-left: 0;
        text-align: center;
        width: 47px;

        ${mediaQuery.from(breakpoints.medium)} {
            margin-left: 0;
        }
    }

    > * {
        margin-left: 0;
    }

    ${({ mhhStyle }) => mhhStyle && css`
        flex-shrink: 0;
        gap: ${gutterSizes.xlarge};
        height: ${mhhActionHeight};
        margin-left: 0;

        ${NavLoginButtonWrapper} {
            width: ${mhhActionItemSize};
        }
    `}
`;

export const SearchBarWrapper = styled('div', { shouldForwardProp })`
    flex: 100%;
    display: flex;
    align-items: center;

    ${mediaQuery.from(breakpoints.large)} {
        order: 1;
        flex: auto;
    }

    ${mediaQuery.between(breakpoints.large, breakpoints.xlarge)} {
        padding: 0 ${gutterSizes.xxlarge};
    }

    ${mediaQuery.from(breakpoints.xlarge)} {
        padding: 0 ${gutterSizes.large};
    }
`;
